import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteContactFile } from 'api/contacts/single';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ContactFile } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const DeleteContactFile = ({
    children,
    file,
}: {
    children: React.ReactNode;
    file: ContactFile;
}) => {
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const toast = useToast();
    const queryClient = useQueryClient();

    const { mutate: deleteFile, isPending: isDeletingFile } = useMutation({
        mutationFn: (fileId: number) => deleteContactFile(accountId, contact.id, fileId),
        onSuccess: () => {
            toast.success('Successfully deleted contact file.');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactFiles(accountId, contact.id),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            toast.error(data?.message ?? 'Failed to delete contact file.');
        },
    });

    return (
        <AlertDialog.Root type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete file</AlertDialog.Title>
                        <AlertDialog.Description>
                            <p>
                                Are you sure you want to delete "{file.name}
                                "?
                            </p>
                            <p>You will not be able to undo this action.</p>
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={() => deleteFile(file.id)}
                            disabled={isDeletingFile}
                        >
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = DeleteContactFile;
const Trigger = AlertDialog.Trigger;
const DeleteContactFileTrigger = Trigger;

export {
    Root,
    Trigger,
    //
    DeleteContactFile,
    DeleteContactFileTrigger,
};
